/**
 * Lightbox reusable. Al abrirlo, la imagen aparece con fade + scale
 * desde 92% → 100%. Cierra con ESC, click en el fondo, o el botón X.
 *
 * Minimalista a propósito — sin carousel, zoom-pan ni focus trap. Si en
 * algún momento queremos esas features saltamos a una lib dedicada
 * (yet-another-react-lightbox, photoswipe).
 */

import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface Props {
  src: string;
  alt: string;
  onClose: () => void;
}

export const ImageLightbox = ({ src, alt, onClose }: Props) => {
  // `open=false` en el primer render y cambia a `true` en el siguiente
  // frame. Eso permite que las clases de Tailwind activen la transición
  // de entrada (sin el timeout queda snap instantáneo).
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    // Bloquea el scroll del body mientras el lightbox está abierto.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Imagen ampliada: ${alt}`}
      onClick={(e) => {
        // Cierra al hacer click en el fondo. Click sobre la imagen en sí
        // no cierra — dejamos al usuario "quedarse" mirando.
        if (e.target === e.currentTarget) onClose();
      }}
      className={
        'fixed inset-0 z-50 flex items-center justify-center p-6 transition-opacity duration-200 ' +
        (open ? 'bg-fg/80 opacity-100' : 'bg-fg/0 opacity-0')
      }
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute right-4 top-4 flex size-10 items-center justify-center rounded-full bg-fg-inverse/10 text-fg-inverse backdrop-blur hover:bg-fg-inverse/20"
      >
        <X className="size-5" />
      </button>

      <img
        src={src}
        alt={alt}
        className={
          // Caja centrada: máximo ~500px o el 70% del viewport, lo que
          // sea menor. object-contain preserva aspect ratio dentro de
          // esa caja — las fotos verticales no se estiran.
          // `bg-white` actúa como "paspartú" para PNGs/WebP con fondo
          // transparente — sin esto el backdrop oscuro se ve a través
          // de ellos y el producto pierde contraste. Imágenes opacas no
          // lo notan (el fondo queda cubierto por los píxeles del JPG).
          'max-h-[min(70vh,500px)] max-w-[min(70vw,500px)] rounded-lg bg-white object-contain shadow-2xl transition-all duration-250 ease-out ' +
          (open ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-0')
        }
      />
    </div>
  );
};
